import type { LatLngTuple } from "leaflet";
import type { Device, TrapSite } from "../../types";
import type { MarkerData } from "../../types/components/map";
import type { EditorDevice } from "./TrapsiteEditor/types";

export type AlertCounts = { [device: string]: number };

export const joinTrapSiteMarkers = (
  trapSites: Partial<TrapSite>[],
  devices: Partial<Device>[],
  maintenanceCounts: AlertCounts,
  detectionCounts: AlertCounts,
): MarkerData[] => {
  const deviceAgg = devices.map((device) => {
    const { uuid, display_name, trap_site_id, gps } = device;

    return {
      uuid: uuid || "",
      deviceName: display_name || "N/A",
      maintainCount: maintenanceCounts[device?.uuid || ""] || 0,
      detectionCount: detectionCounts[device?.uuid || ""] || 0,
      trapSite: trap_site_id || "",
      gps,
    };
  });

  return trapSites.map((trapSite) => {
    const devicesInTrapSite = deviceAgg.filter(
      (device) => device.trapSite === trapSite._id,
    );
    const detectionCount = devicesInTrapSite.reduce(
      (sum, d) => sum + (d.detectionCount || 0),
      0,
    );
    const maintainCount = devicesInTrapSite.reduce(
      (sum, d) => sum + (d.maintainCount || 0),
      0,
    );
    const trapCount = devicesInTrapSite.length;

    return {
      id: trapSite._id,
      displayName: trapSite.display_name,
      position: [
        trapSite.gps?.lat || 0,
        trapSite.gps?.lon || 0,
      ] as LatLngTuple,
      warning: maintainCount > 0 || detectionCount > 0,
      detectionsCount: detectionCount,
      maintainCount: maintainCount,
      trapCount: trapCount,
      devicesInTrapSite: devicesInTrapSite,
      popupOpen: true,
    };
  });
};

export const narrowMarkersToTrapSite = (
  markers: MarkerData[],
  trapSite: Partial<TrapSite> | undefined,
  deviceId: string | undefined,
): MarkerData[] => {
  if (!trapSite) return markers;

  const match = markers.find((m) => m.id === trapSite._id);
  if (!match?.devicesInTrapSite?.length) {
    return match ? [match] : [];
  }

  const relevantDevices = deviceId
    ? match.devicesInTrapSite.filter((d) => d.uuid === deviceId)
    : match.devicesInTrapSite;

  const withGps = relevantDevices.filter((d) => d.gps?.lat && d.gps?.lon);
  const withoutGps = relevantDevices.filter(
    (d) => !d.gps?.lat || !d.gps?.lon,
  );

  const deviceMarkers: MarkerData[] = withGps.map((device) => ({
    deviceUuid: device.uuid,
    displayName: device.deviceName,
    position: [device.gps!.lat, device.gps!.lon] as LatLngTuple,
    warning:
      (device.maintainCount || 0) > 0 || (device.detectionCount || 0) > 0,
    detectionsCount: device.detectionCount || 0,
    maintainCount: device.maintainCount || 0,
    trapCount: 1,
    markerType: "device" as const,
    gps: device.gps,
    popupOpen: true,
  }));

  if (withoutGps.length > 0) {
    deviceMarkers.push({
      displayName: match.displayName,
      position: match.position,
      warning: withoutGps.some(
        (d) => (d.maintainCount || 0) > 0 || (d.detectionCount || 0) > 0,
      ),
      detectionsCount: withoutGps.reduce(
        (s, d) => s + (d.detectionCount || 0),
        0,
      ),
      maintainCount: withoutGps.reduce((s, d) => s + (d.maintainCount || 0), 0),
      trapCount: withoutGps.length,
      popupOpen: true,
    });
  }

  return deviceMarkers;
};

export const toEditorDevices = (
  devices: Partial<Device>[] | undefined,
  trapSites: Partial<TrapSite>[] | undefined,
): EditorDevice[] => {
  if (!devices) return [];
  const siteNames = new Map(
    (trapSites || []).map((t) => [t._id, t.display_name]),
  );
  return devices.map((device) => ({
    uuid: device.uuid || "",
    displayName: device.display_name || device.uuid || "",
    trapSiteId: device.trap_site_id || null,
    trapSiteName: device.trap_site_id
      ? siteNames.get(device.trap_site_id) || null
      : null,
    // a device's own position, distinct from its site's; a trap can sit a field away from the site address
    gps: device.gps ?? null,
  }));
};

export const resolveMapCenter = (
  deviceId: string | undefined,
  devices: Partial<Device>[] | undefined,
  trapSite: Partial<TrapSite> | undefined,
  fallback: LatLngTuple,
): LatLngTuple => {
  if (deviceId) {
    const device = devices?.find((d) => d.uuid === deviceId);
    if (device?.gps?.lat && device?.gps?.lon) {
      return [device.gps.lat, device.gps.lon];
    }
  }

  if (trapSite?.gps?.lat && trapSite?.gps?.lon) {
    return [trapSite.gps.lat, trapSite.gps.lon];
  }
  return fallback;
};
